/**
 * USVI Pirate's Guild — Plan Definitions (single source of truth)
 *
 * This file is the canonical definition for every traveler and business tier
 * displayed on the website. Pages render pricing UI from these objects until
 * the corresponding Wix plans are created and `wixPlanId` values are pasted in.
 *
 * Once Wix admin work is complete:
 *   1. Open WIX_PLANS_SPEC.md at the repo root.
 *   2. Copy each generated Wix plan ID and slug into the matching tier below.
 *   3. PlansContext / PlansManager will then map active subscriptions to the
 *      new tier names automatically.
 *
 * IMPORTANT: keep `name` exactly equal to the Wix plan name. Membership checks
 * in PlansContext.js (getActiveTravelerTier / getActiveBusinessTier) match by
 * name, and functions/planTiers.js mirrors these names server-side.
 */

// Stable internal identifiers used across the codebase. These do NOT change
// when Wix names/prices change. Renamed identifiers for the V2 model:
export const TIER_KEYS = Object.freeze({
  // Traveler tiers (audience = traveler)
  DECKHAND: "DECKHAND",
  ADVENTURER_7DAY: "ADVENTURER_7DAY",
  ADVENTURER_ANNUAL: "ADVENTURER_ANNUAL",
  TRAVELER_PLUS: "TRAVELER_PLUS",
  // Business tiers (audience = business)
  CREW_LISTING: "CREW_LISTING",
  FEATURED_CAPTAIN: "FEATURED_CAPTAIN",
  PORT_SPONSOR: "PORT_SPONSOR",
});

// Audience tags
export const AUDIENCE = Object.freeze({
  TRAVELER: "traveler",
  BUSINESS: "business",
});

// Billing cycle helpers (match Wix `cycleDuration.unit` values)
export const BILLING = Object.freeze({
  ONE_TIME: "ONE_TIME",
  WEEK: "WEEK",
  MONTH: "MONTH",
  YEAR: "YEAR",
});

/**
 * Each tier has the shape:
 * {
 *   key:           stable internal identifier (TIER_KEYS.*)
 *   name:          public display name AND Wix plan name (must match Wix exactly)
 *   audience:      AUDIENCE.TRAVELER | AUDIENCE.BUSINESS
 *   priceUsd:      number | null (null for Free tiers)
 *   billing:       BILLING.* (ONE_TIME for Deckhand free + 7-day pass; YEAR for annual; MONTH for business)
 *   billingLabel:  short human label e.g. "Free", "for 7 days", "/year", "/month"
 *   tagline:       one-line value statement
 *   description:   paragraph for marketing pages
 *   features:      string[] of bullet points
 *   cta:           { label, href }  primary CTA for this tier
 *   highlight:     boolean — flag for "most popular" / featured card
 *   wixPlanId:     string | null   — paste from Wix admin after plan is created
 *   wixPlanSlug:   string | null   — paste from Wix admin after plan is created
 *   legacyAliases: string[] — older Wix plan names that grant equivalent access
 * }
 */

export const TRAVELER_TIERS = [
  {
    key: TIER_KEYS.DECKHAND,
    name: "Deckhand Pass",
    audience: AUDIENCE.TRAVELER,
    priceUsd: 0,
    billing: BILLING.ONE_TIME,
    billingLabel: "Free",
    tagline: "Start exploring the USVI for free.",
    description:
      "Start exploring the USVI with free access to participating businesses, basic Guild deals, event updates, and the digital Pirate's Map.",
    features: [
      "Free traveler account",
      "Basic partner directory",
      "Standard Guild deals",
      "Basic digital map",
      "Event notifications",
      "Earn Doubloons through visits, referrals, and treasure hunts",
    ],
    cta: { label: "Join Free", href: "/signup?tier=deckhand" },
    highlight: false,
    wixPlanId: null,
    wixPlanSlug: null,
    legacyAliases: [],
    limits: { favoriteCap: 10, doubloonMultiplier: 1 },
    perks: [
      { id: "directory",      label: "Browse partner directory",        status: "live" },
      { id: "standardDeals",  label: "Standard Guild deals",             status: "live" },
      { id: "basicMap",       label: "Basic digital treasure map",       status: "live" },
      { id: "eventFeed",      label: "Event notifications",              status: "live" },
      { id: "doubloons1x",    label: "Earn Doubloons (1\u00d7 base rate)", status: "live" },
      { id: "favorites10",    label: "Save up to 10 favorites",          status: "live" },
    ],
  },
  {
    key: TIER_KEYS.ADVENTURER_7DAY,
    name: "7-Day Adventurer Pass",
    audience: AUDIENCE.TRAVELER,
    priceUsd: 19.99,
    billing: BILLING.ONE_TIME,
    billingLabel: "for 7 days",
    tagline: "Perfect for a short trip.",
    description:
      "Perfect for visitors here for a short trip. Unlock premium deals, full treasure maps, bonus Doubloons, and curated island recommendations for your stay.",
    features: [
      "Everything in Deckhand Pass",
      "Premium traveler-only deals",
      "Full interactive treasure map",
      "7-day itinerary suggestions",
      "VIP event invites during the active pass window",
      "Bonus Doubloons",
      "Early access to limited-time offers",
    ],
    cta: { label: "Start 7-Day Pass", href: "/traveler-passes#adventurer-7day" },
    highlight: true,
    wixPlanId: null,
    wixPlanSlug: null,
    legacyAliases: [],
    limits: { favoriteCap: 50, doubloonMultiplier: 2 },
    perks: [
      { id: "premiumDeals",   label: "Premium traveler-only deals",      status: "live" },
      { id: "fullMap",        label: "Full interactive treasure map",    status: "live" },
      { id: "doubloons2x",    label: "2\u00d7 Doubloons on every visit",  status: "live" },
      { id: "favorites50",    label: "Save up to 50 favorites",          status: "live" },
      { id: "itinerary7d",    label: "7-day itinerary suggestions",       status: "coming-soon" },
      { id: "vipInvites",     label: "VIP event invites during pass",     status: "live" },
      { id: "earlyOffers",    label: "Early access to limited offers",    status: "coming-soon" },
    ],
  },
  {
    key: TIER_KEYS.ADVENTURER_ANNUAL,
    name: "Pirate Adventurer Annual Pass",
    audience: AUDIENCE.TRAVELER,
    priceUsd: 59,
    billing: BILLING.YEAR,
    billingLabel: "/year",
    tagline: "Built for locals and repeat visitors.",
    description:
      "Built for locals, repeat visitors, yacht crews, seasonal workers, and frequent island explorers.",
    features: [
      "Everything in the 7-Day Adventurer Pass",
      "Year-round premium deal access",
      "Monthly featured offers",
      "Member-only rewards",
      "Higher Doubloon earning rate",
      "Local event access",
      "Seasonal island guides",
    ],
    cta: {
      label: "Become an Annual Adventurer",
      href: "/traveler-passes#adventurer-annual",
    },
    highlight: false,
    // Legacy "Traveler" plan from V1 grants equivalent access during migration:
    wixPlanId: null,
    wixPlanSlug: null,
    legacyAliases: ["Traveler"],
    limits: { favoriteCap: 100, doubloonMultiplier: 2 },
    perks: [
      { id: "premiumDealsYear", label: "Year-round premium deal access",  status: "live" },
      { id: "doubloons2x",      label: "2\u00d7 Doubloons on every visit", status: "live" },
      { id: "favorites100",     label: "Save up to 100 favorites",        status: "live" },
      { id: "monthlyFeature",   label: "Monthly featured offers",          status: "coming-soon" },
      { id: "memberRewards",    label: "Member-only rewards",              status: "coming-soon" },
      { id: "localEvents",      label: "Local event access",               status: "live" },
      { id: "seasonalGuides",   label: "Seasonal island guides",           status: "coming-soon" },
    ],
  },
  {
    key: TIER_KEYS.TRAVELER_PLUS,
    name: "Traveler Plus",
    audience: AUDIENCE.TRAVELER,
    priceUsd: 99,
    billing: BILLING.YEAR,
    billingLabel: "/year",
    tagline: "Concierge access, VIP events, exclusive experiences.",
    description:
      "The premium pass for travelers who want deeper local access, concierge help, VIP events, and exclusive experiences.",
    features: [
      "Everything in Pirate Adventurer Annual Pass",
      "Concierge travel assistance",
      "Premium digital guides and itineraries",
      "VIP events and exclusive meetups",
      "Early access to new deals",
      "Exclusive local experiences",
      "Optional roommate finder, job search, and listing features",
    ],
    cta: { label: "Upgrade to Traveler Plus", href: "/traveler-passes#plus" },
    highlight: false,
    wixPlanId: null,
    wixPlanSlug: null,
    // Legacy V1 name was identical:
    legacyAliases: ["Traveler Plus"],
    limits: { favoriteCap: 200, doubloonMultiplier: 2, earlyRsvpHours: 24 },
    perks: [
      { id: "doubloons2x",      label: "2\u00d7 Doubloons on every visit",  status: "live" },
      { id: "favorites200",     label: "Save up to 200 favorites",         status: "live" },
      { id: "earlyRsvp24h",     label: "24h early RSVP on events",         status: "live" },
      { id: "concierge",        label: "Concierge travel assistance",      status: "coming-soon" },
      { id: "premiumGuides",    label: "Premium digital guides",           status: "coming-soon" },
      { id: "vipEvents",        label: "VIP events and exclusive meetups", status: "live" },
      { id: "earlyDeals",       label: "Early access to new deals",        status: "coming-soon" },
      { id: "localExperiences", label: "Exclusive local experiences",      status: "coming-soon" },
      { id: "roommateJobs",     label: "Roommate finder, job board",       status: "live" },
    ],
  },
];

export const BUSINESS_TIERS = [
  {
    key: TIER_KEYS.CREW_LISTING,
    name: "Crew Listing",
    audience: AUDIENCE.BUSINESS,
    priceUsd: 0,
    billing: BILLING.ONE_TIME,
    billingLabel: "Free",
    tagline: "Get discovered by Guild travelers.",
    description:
      "A free listing for local businesses that want to be discovered by Guild travelers.",
    features: [
      "Basic business profile",
      "Category listing",
      "One standard Guild offer or perk",
      "Basic map placement",
      "Basic QR code or redemption tracking",
      "Eligibility to appear in traveler searches",
    ],
    cta: { label: "List Your Business Free", href: "/signup?audience=business&tier=crew" },
    highlight: false,
    wixPlanId: null,
    wixPlanSlug: null,
    legacyAliases: [],
    limits: { sortBoost: 0, hasMapDecoration: false },
    perks: [
      { id: "basicProfile",   label: "Basic business profile",         status: "live" },
      { id: "category",       label: "Category listing",                status: "live" },
      { id: "oneOffer",       label: "One standard Guild offer or perk", status: "live" },
      { id: "basicMap",       label: "Basic map placement",             status: "live" },
      { id: "qrTracking",     label: "Basic QR code & redemption tracking", status: "live" },
      { id: "searchable",     label: "Eligible for traveler searches",   status: "live" },
    ],
  },
  {
    key: TIER_KEYS.FEATURED_CAPTAIN,
    name: "Featured Captain",
    audience: AUDIENCE.BUSINESS,
    priceUsd: 79,
    billing: BILLING.MONTH,
    billingLabel: "/month",
    // Annual variant pricing surfaced in marketing copy where relevant
    annualPriceUsd: 790,
    tagline: "Stronger promotion, featured placement, real analytics.",
    description:
      "For businesses that want stronger promotion, featured placement, and measurable traveler engagement.",
    features: [
      "Enhanced business profile",
      "Featured category placement",
      "Featured map pin",
      "Monthly social media feature",
      "Inclusion in selected email/text campaigns",
      "QR code campaign tracking",
      "Redemption analytics",
      "Ability to create limited-time offers",
      "Ability to run slow-day or happy-hour promotions",
      "Priority eligibility for event promotion",
    ],
    cta: { label: "Become a Featured Captain", href: "/for-businesses#featured-captain" },
    highlight: true,
    wixPlanId: null,
    wixPlanSlug: null,
    // Legacy V1 "Ship Captain (Business Owner)" mapped here:
    legacyAliases: ["Ship Captain (Business Owner)"],
    limits: { sortBoost: 100, hasMapDecoration: true },
    perks: [
      { id: "enhancedProfile",  label: "Enhanced business profile",     status: "live" },
      { id: "featuredSort",     label: "Featured-Captain sort boost",   status: "live" },
      { id: "featuredPin",      label: "Featured map pin decoration",   status: "live" },
      { id: "qrAnalytics",      label: "QR & redemption analytics",     status: "live" },
      { id: "limitedOffers",    label: "Limited-time offers",            status: "live" },
      { id: "happyHour",        label: "Slow-day / happy-hour promotions", status: "live" },
      { id: "socialFeature",    label: "Monthly social media feature",  status: "coming-soon" },
      { id: "emailCampaigns",   label: "Inclusion in email/text campaigns", status: "coming-soon" },
      { id: "eventPriority",    label: "Priority for event promotion",  status: "coming-soon" },
    ],
  },
  {
    key: TIER_KEYS.PORT_SPONSOR,
    name: "Port Sponsor",
    audience: AUDIENCE.BUSINESS,
    priceUsd: 199,
    billing: BILLING.MONTH,
    billingLabel: "/month",
    priceNote: "Custom pricing available — starting at $199/month",
    tagline: "Premium visibility across maps, events, and campaigns.",
    description:
      "For businesses that want premium visibility across maps, events, campaigns, and sponsored treasure rewards.",
    features: [
      "Sponsored map placement",
      "Sponsored category placement",
      "Sponsored Doubloon rewards",
      "Event sponsorship opportunities",
      "Featured placement in seasonal campaigns",
      "Dedicated campaign reporting",
      "Optional booking or lead-generation support",
    ],
    cta: { label: "Request Sponsorship Info", href: "/contact?topic=port-sponsor" },
    highlight: false,
    wixPlanId: null,
    wixPlanSlug: null,
    legacyAliases: [],
    limits: { sortBoost: 200, hasMapDecoration: true, hasPortDecoration: true },
    perks: [
      { id: "sponsoredMap",     label: "Sponsored map placement",         status: "live" },
      { id: "portDecoration",   label: "Port Sponsor map decoration",     status: "live" },
      { id: "sortBoostMax",     label: "Top-of-results sort boost",       status: "live" },
      { id: "sponsoredCategory",label: "Sponsored category placement",    status: "coming-soon" },
      { id: "sponsoredDoubloons", label: "Sponsored Doubloon rewards",    status: "coming-soon" },
      { id: "eventSponsorship", label: "Event sponsorship opportunities", status: "coming-soon" },
      { id: "seasonalCampaigns",label: "Featured in seasonal campaigns",  status: "coming-soon" },
      { id: "campaignReporting",label: "Dedicated campaign reporting",    status: "coming-soon" },
      { id: "leadGen",          label: "Optional booking / lead-gen",     status: "coming-soon" },
    ],
  },
];

export const ALL_TIERS = [...TRAVELER_TIERS, ...BUSINESS_TIERS];

/** Lookup tier by stable internal key. */
export function getTierByKey(key) {
  return ALL_TIERS.find((t) => t.key === key) || null;
}

/** Lookup tier by Wix plan name (or any of its legacy aliases). */
export function getTierByPlanName(planName) {
  if (!planName) return null;
  return (
    ALL_TIERS.find(
      (t) => t.name === planName || (t.legacyAliases || []).includes(planName)
    ) || null
  );
}

/**
 * Classify a plan relative to a member's current active tiers, for CTA labels
 * everywhere a member can change plans. Compares within the plan's own audience
 * (traveler vs business), so a business plan is judged against the business tier.
 *
 * @param {string} planName
 * @param {object|null} activeTravelerTier - from getActiveTravelerTier()
 * @param {object|null} activeBusinessTier - from getActiveBusinessTier()
 * @returns {{ relation: "current"|"upgrade"|"downgrade"|"new", label: string }}
 */
export function classifyPlanForMember(planName, activeTravelerTier, activeBusinessTier) {
  const tier = getTierByPlanName(planName);
  if (!tier) return { relation: "new", label: "Subscribe" };
  const current =
    tier.audience === AUDIENCE.BUSINESS ? activeBusinessTier : activeTravelerTier;
  if (!current) return { relation: "new", label: "Subscribe" };
  if (current.key === tier.key) return { relation: "current", label: "Current plan" };
  const list =
    tier.audience === AUDIENCE.BUSINESS ? BUSINESS_TIERS : TRAVELER_TIERS;
  const curRank = list.findIndex((t) => t.key === current.key);
  const planRank = list.findIndex((t) => t.key === tier.key);
  if (planRank > curRank) return { relation: "upgrade", label: "Upgrade" };
  return { relation: "downgrade", label: "Downgrade" };
}

/**
 * Given a member's active plan-name list, return the highest-rank traveler
 * tier they hold (or null). Order in TRAVELER_TIERS = ascending value, so the
 * last match wins.
 */
export function highestTravelerTier(activePlanNames) {
  let best = null;
  for (const tier of TRAVELER_TIERS) {
    const matches = activePlanNames.some(
      (n) => n === tier.name || (tier.legacyAliases || []).includes(n)
    );
    if (matches) best = tier;
  }
  return best;
}

/** Same idea for business tiers. */
export function highestBusinessTier(activePlanNames) {
  let best = null;
  for (const tier of BUSINESS_TIERS) {
    const matches = activePlanNames.some(
      (n) => n === tier.name || (tier.legacyAliases || []).includes(n)
    );
    if (matches) best = tier;
  }
  return best;
}

/** Format tier price for display. */
export function formatTierPrice(tier) {
  if (!tier) return "";
  if (tier.priceUsd === 0 || tier.priceUsd == null) return "Free";
  const value = `$${tier.priceUsd}`;
  return tier.billingLabel ? `${value} ${tier.billingLabel}`.trim() : value;
}

// ---------------------------------------------------------------------------
// Perks + limits helpers (Phase 6B)
// ---------------------------------------------------------------------------

/** Get the structured perks array for a tier, or [] if none defined. */
export function getTierPerks(tier) {
  return Array.isArray(tier?.perks) ? tier.perks : [];
}

/** True if the tier publishes the given perk id with status:'live'. */
export function hasLivePerk(tier, perkId) {
  return getTierPerks(tier).some(
    (p) => p && p.id === perkId && p.status === "live"
  );
}

/** Favorite-cap for a traveler tier. Falls back to the Deckhand cap. */
export function favoriteCapFor(tier) {
  const cap = tier?.limits?.favoriteCap;
  if (Number.isFinite(cap)) return cap;
  return 10; // Deckhand default
}

/** Doubloon earning multiplier for a traveler tier. */
export function doubloonMultiplierFor(tier) {
  const m = tier?.limits?.doubloonMultiplier;
  return Number.isFinite(m) && m > 0 ? m : 1;
}

/** Sort-boost amount for a business tier (added to relevance score). */
export function sortBoostFor(tier) {
  const b = tier?.limits?.sortBoost;
  return Number.isFinite(b) ? b : 0;
}

// ---------------------------------------------------------------------------
// Perk gating (tier-inheritance aware) — foundation for tier perks.
// A perk id appears on exactly one tier list; higher tiers of the same audience
// inherit every lower tier's perks ("Everything in <lower tier>"). These helpers
// resolve grants + the required tier so the UI can gate features and render
// accurate upgrade prompts.
// ---------------------------------------------------------------------------

/**
 * Which audience defines a perk id (the tier list it appears in).
 * @returns {"traveler"|"business"|null}
 */
export function perkAudience(perkId) {
  if (TRAVELER_TIERS.some((t) => getTierPerks(t).some((p) => p.id === perkId))) {
    return AUDIENCE.TRAVELER;
  }
  if (BUSINESS_TIERS.some((t) => getTierPerks(t).some((p) => p.id === perkId))) {
    return AUDIENCE.BUSINESS;
  }
  return null;
}

/**
 * True if `activeTier` grants the perk, honoring tier inheritance: any
 * same-audience tier at or below the active tier that publishes the perk with
 * `status: 'live'` grants it. A `coming-soon` perk never grants access.
 */
export function tierGrantsPerk(activeTier, perkId) {
  if (!activeTier || !perkId) return false;
  const list =
    activeTier.audience === AUDIENCE.BUSINESS ? BUSINESS_TIERS : TRAVELER_TIERS;
  const activeRank = list.findIndex((t) => t.key === activeTier.key);
  if (activeRank < 0) return false;
  for (let i = 0; i <= activeRank; i += 1) {
    if (getTierPerks(list[i]).some((p) => p && p.id === perkId && p.status === "live")) {
      return true;
    }
  }
  return false;
}

/**
 * The lowest-ranked tier that publishes the perk `status:'live'` — the tier a
 * user must reach to unlock it. Returns the tier object, or null if no tier
 * offers it live yet. Used for "Requires <tier>" upgrade prompts.
 */
export function requiredTierForPerk(perkId) {
  const audience = perkAudience(perkId);
  if (!audience) return null;
  const list = audience === AUDIENCE.BUSINESS ? BUSINESS_TIERS : TRAVELER_TIERS;
  return (
    list.find((t) =>
      getTierPerks(t).some((p) => p.id === perkId && p.status === "live"),
    ) || null
  );
}

/**
 * True if `activeTier` meets or exceeds `minTierKey` (same audience). A null or
 * empty `minTierKey` means "no gate" — everyone passes. Used for content gated
 * by a minimum tier (e.g. members-only events) rather than a named perk.
 */
export function tierMeetsMinimum(activeTier, minTierKey) {
  if (!minTierKey) return true;
  const minTier = getTierByKey(minTierKey);
  if (!minTier) return true; // unknown key — don't block
  if (!activeTier || activeTier.audience !== minTier.audience) return false;
  const list =
    minTier.audience === AUDIENCE.BUSINESS ? BUSINESS_TIERS : TRAVELER_TIERS;
  const activeRank = list.findIndex((t) => t.key === activeTier.key);
  const minRank = list.findIndex((t) => t.key === minTier.key);
  return activeRank >= 0 && minRank >= 0 && activeRank >= minRank;
}

/**
 * Traveler tiers (above free Deckhand) that can gate content, for admin
 * pickers. Ascending order. Mirrors EVENT_MIN_TIERS in functions/eventsFn.js.
 */
export const GATING_TRAVELER_TIERS = TRAVELER_TIERS.filter(
  (t) => t.key !== TIER_KEYS.DECKHAND,
).map((t) => ({ key: t.key, name: t.name }));

